// Sign out of all devices — the self-service answer to "I think my account is
// compromised". Before this existed the only ways to end every session were an
// owner resetting the password or an admin switching mfa_required on.
// Traceability: FR-AUTH-008, US-AUTH-010, QA 2.3.12.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, ConfirmDialog, useToast } from '@bbpos/ui';
import { useAuthStore } from '@/stores/use-auth-store';
import { GLASS_FORM, cn } from '@/constants/design-tokens';

function ShieldIcon({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  );
}

export function SignOutAllDevicesCard() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const logoutAll = useAuthStore((s) => s.logoutAll);
  const [confirming, setConfirming] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);

  const handleConfirm = async () => {
    setIsSigningOut(true);
    try {
      const sessionsEnded = await logoutAll();
      toast({
        title: `Signed out of ${sessionsEnded} ${sessionsEnded === 1 ? 'device' : 'devices'}`,
        variant: 'success',
      });
      navigate('/login');
    } catch {
      // No session was ended, so the user stays signed in and can retry.
      toast({ title: 'Could not sign out of all devices', variant: 'error' });
    } finally {
      setIsSigningOut(false);
      setConfirming(false);
    }
  };

  // The dialog sits outside the card: the card's backdrop-blur (and slide-up
  // transform) would otherwise become the containing block for the dialog's
  // position:fixed and clip it to the card.
  return (
    <>
      <div className={cn(GLASS_FORM, 'p-5 flex items-start gap-4 animate-slide-up')}>
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-warning-500 to-danger-500 flex items-center justify-center text-white flex-shrink-0 shadow-sm">
          <ShieldIcon />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-neutral-900 dark:text-neutral-100">Sign out of all devices</p>
          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
            Ends every session on your account — phones, tills and this browser. Use it if you think
            someone else has access.
          </p>
        </div>
        <Button
          variant="danger"
          size="sm"
          onClick={() => setConfirming(true)}
          disabled={isSigningOut}
        >
          Sign out everywhere
        </Button>
      </div>

      <ConfirmDialog
        open={confirming}
        title="Sign out of all devices?"
        description="Every device signed in as you is logged out immediately, including this one — you will be sent back to the login screen. Any unsaved work on those devices is lost."
        confirmLabel="Sign out everywhere"
        variant="danger"
        loading={isSigningOut}
        onConfirm={handleConfirm}
        onCancel={() => setConfirming(false)}
      />
    </>
  );
}
